/**
 * Lane C0-SOUND, the gaps the audit found (docs/research/C0_SOUND.md §5): a lock form taking hold and letting go, a
 * duel challenge, stepping into and out of a safe zone, a secret found (a vista), a trial's rings, loot dropping by
 * rarity — and the outdoors by time of day: a breeze bed, birds by day, crickets and frogs at night (sfx/game.ts).
 */
import { BELL } from '../kit';
import { registerSfx } from '../registry';
import { chime, drum, gong, note, ROOT_D, strum } from '../voices';

// ───────── duel and lock forms ─────────

registerSfx('combat.lock.on', { label: 'ล็อกเป้า (เข้าท่าคู่)', cat: 'combat', vary: 0.02, gap: 0.3 }, (k) => {
  // A grip taking hold: a tight leather snap, a low clamp and a short rising ring that says "bound".
  k.noise({ dur: 0.04, vol: 0.25, attack: 0.001, f: 1800, q: 2 });
  k.thump({ f0: 190, f1: 75, dur: 0.18, vol: 0.32 });
  k.metal({ f: 880, at: 0.04, dur: 0.5, vol: 0.05, partials: BELL });
  k.tone({ f: note(ROOT_D, 2), f1: note(ROOT_D, 4), at: 0.05, dur: 0.3, vol: 0.05 });
});

registerSfx('combat.lock.drop', { label: 'หลุดล็อกเป้า', cat: 'combat', vary: 0.03, gap: 0.3 }, (k) => {
  k.tone({ f: note(ROOT_D, 4), f1: note(ROOT_D, 0), dur: 0.25, vol: 0.05 });
  k.noise({ dur: 0.15, vol: 0.08, attack: 0.01, f: 1600, f1: 500, q: 2 });
});

registerSfx('duel.challenge', { label: 'ท้าประลอง', cat: 'combat', vary: 0, gap: 1, preview: 1.6 }, (k) => {
  // Two war-drum strokes and a small gong: a glove thrown down.
  drum(k, { f: 75, vol: 0.4 });
  drum(k, { f: 75, at: 0.2, vol: 0.45 });
  gong(k, { f: 196, at: 0.42, dur: 1, vol: 0.12 });
});

// ───────── places ─────────

registerSfx('world.safeZone.enter', { label: 'เข้าเขตปลอดภัย', cat: 'world', vary: 0, gap: 1, preview: 1.6 }, (k) => {
  // A wind chime and a settling breath: safe.
  [0, 2, 4].forEach((d, i) => chime(k, { f: note(ROOT_D * 2, d), at: i * 0.09, vol: 0.05, dur: 1 }));
  k.noise({ color: 'pink', dur: 0.8, vol: 0.06, attack: 0.3, type: 'lowpass', f: 1200, f1: 500 });
});

registerSfx('world.safeZone.leave', { label: 'ออกจากเขตปลอดภัย', cat: 'world', vary: 0, gap: 1, preview: 1.2 }, (k) => {
  // Lower and open: the road beyond.
  [4, 2, -1].forEach((d, i) => chime(k, { f: note(ROOT_D, d), at: i * 0.1, vol: 0.05, dur: 0.8 }));
  drum(k, { f: 70, at: 0.32, vol: 0.18 });
});

registerSfx('world.vista', { label: 'พบจุดชมวิว', cat: 'world', vary: 0, gap: 2, preview: 2.6 }, (k) => {
  // The wind up high and a guzheng run.
  k.noise({ color: 'pink', dur: 2, vol: 0.1, attack: 0.5, f: 500, f1: 1400, f2: 600, sweep: 1, q: 1.5 });
  strum(k, ROOT_D, [0, 1, 2, 3, 4, 5, 7], { at: 0.15, gap: 0.08, vol: 0.12, dur: 1.6, bright: 0.6 });
});

registerSfx('world.trial.start', { label: 'เริ่มทดสอบวิชาตัวเบา', cat: 'world', vary: 0, gap: 1, preview: 1.4 }, (k) => {
  woodblock3(k);
  chime(k, { f: note(ROOT_D * 2, 4), at: 0.3, vol: 0.08, dur: 0.9 });
});

registerSfx('world.trial.ring', { label: 'ผ่านห่วงทดสอบ', cat: 'world', vary: 0, gap: 0.1 }, (k) => {
  // A bright ring through the air; the caller raises its pitch ring by ring.
  chime(k, { f: note(ROOT_D * 2, 2), vol: 0.08, dur: 0.6 });
  k.noise({ dur: 0.2, vol: 0.08, attack: 0.05, f: 2000, f1: 6000, q: 3 });
});

function woodblock3(k: Parameters<Parameters<typeof registerSfx>[2]>[0]): void {
  for (let i = 0; i < 3; i++) k.tone({ f: 900, f1: 830, at: i * 0.12, dur: 0.08, vol: 0.12, attack: 0.001 });
}

// ───────── loot dropping by rarity ─────────

/** Rarities (shared data/items.ts) from plain to mythic: a heavier, brighter fall the rarer. */
export const DROP_RARITY = ['common', 'fine', 'rare', 'epic', 'legend', 'mythic'] as const;

DROP_RARITY.forEach((r, i) =>
  registerSfx(
    `econ.drop.${r}`,
    { label: `ของตก (${['ของตลาด', 'งานช่าง', 'ครูช่าง', 'สูตรลับ', 'ตกทอด', 'ฟ้าสร้าง'][i]})`, cat: 'econ', vary: 0.03, gap: 0.08, preview: 0.6 + i * 0.3 },
    (k) => {
      // Something falling on the ground; from rare up a shimmer, from legend a bell.
      k.thump({ f0: 220 - i * 15, f1: 110, dur: 0.1, vol: 0.18 });
      k.click({ f: 2400, vol: 0.1 });
      if (i >= 2)
        for (let n = 0; n < i; n++) chime(k, { f: note(ROOT_D * 2, n + i), at: 0.05 + n * 0.06, vol: 0.03 + i * 0.005, dur: 0.5 + i * 0.15 });
      if (i >= 4) k.metal({ f: note(ROOT_D, i), at: 0.1, dur: 1.2 + i * 0.2, vol: 0.06, partials: BELL });
    },
  ),
);

// ───────── the outdoors by time of day (sfx/game.ts plays these) ─────────

registerSfx('amb.breeze', { label: 'ลมโชยกลางแจ้ง (วน)', cat: 'ambient', loop: true, vary: 0, preview: 4 }, (k) => {
  k.noise({ color: 'pink', dur: Infinity, vol: 0.06, attack: 1.5, type: 'bandpass', f: 600, q: 0.6, trem: [0.15, 0.6] });
  k.noise({ dur: Infinity, vol: 0.012, attack: 1.5, f: 3200, q: 1.5, trem: [0.23, 0.7] });
});

registerSfx('amb.bird', { label: 'นกร้อง', cat: 'ambient', vary: 0.12, gap: 0.3, preview: 1.2 }, (k) => {
  // A small songbird: two to four quick falling-then-rising chirps.
  const n = 2 + Math.floor(k.r() * 3);
  const base = k.range(2600, 3800);
  for (let i = 0; i < n; i++) {
    const at = i * k.range(0.08, 0.14);
    k.tone({ f: base * 1.2, f1: base * 0.8, sweep: 0.04, at, dur: 0.07, vol: 0.03, attack: 0.005 });
    k.tone({ f: base * 0.9, f1: base * 1.3, at: at + 0.04, dur: 0.05, vol: 0.02, attack: 0.004 });
  }
});

registerSfx('amb.cricket', { label: 'จิ้งหรีดยามค่ำ', cat: 'ambient', vary: 0.05, gap: 0.3, preview: 1.4 }, (k) => {
  // Pulsed chirps: a fast tremolo on a high tone, three to five pulses.
  const f = k.range(4200, 4800);
  const n = 3 + Math.floor(k.r() * 3);
  for (let i = 0; i < n; i++) k.tone({ f, at: i * 0.11, dur: 0.07, vol: 0.016, attack: 0.005, vib: [55, 0.004] });
});

registerSfx('amb.frog', { label: 'กบร้องริมน้ำ', cat: 'ambient', vary: 0.1, gap: 0.4, preview: 1 }, (k) => {
  for (let i = 0; i < 2; i++)
    k.tone({ type: 'square', f: k.range(160, 200), f1: 140, at: i * 0.16, dur: 0.1, vol: 0.02, attack: 0.01, out: k.filter('lowpass', 700) });
});
